// src/components/ui/Checkbox.jsx
import React from "react";
import { cn } from "@/utils/cn";

/**
 * Rock-solid checkbox (no nested <button> issues):
 * - A single <div role="checkbox"> (24×24 hit, 20px box).
 * - Keyboard accessible (Space / Enter).
 * - Never bubbles (stops row clicks).
 * - Calls onChange(nextChecked) exactly once.
 */
const Checkbox = React.forwardRef(function Checkbox(
  { checked = false, onChange, disabled = false, className, ariaLabel }, ref
) {
  const toggle = (e) => {
    if (disabled) return;
    e?.stopPropagation?.();
    onChange?.(!checked);
  };

  const onKeyDown = (e) => {
    // prevent the row from also toggling
    e.stopPropagation();

    if (disabled) return;

    // Space/Enter toggles
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      onChange?.(!checked);
    }
  };

  return (
    <div
      ref={ref}
      role="checkbox"
      aria-label={ariaLabel}
      aria-checked={checked}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      onClick={toggle}
      onKeyDown={onKeyDown}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      className={cn(
        "grid place-items-center h-6 w-6 rounded-[6px] select-none",
        checked
          ? "bg-[rgb(var(--afrimed-primary))] shadow-[0_0_0_1px_rgba(143,149,158,0.16)]"
          : "bg-white border border-[rgb(var(--clr-outline))]",
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
        className
      )}
      style={{ WebkitTapHighlightColor: "transparent" }}
    >
      <svg
        className={cn("h-3.5 w-3.5", checked ? "opacity-100" : "opacity-0")}
        viewBox="0 0 20 20"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M5 10.5l3 3 7-7"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
});

export default Checkbox;
